<template>
    <div
        class="internal-login text-center"
        :style="{
            'background-image': `url('/assets/images/fe_background.png')`,
        }"
    >
        <main class="internal-login__content">
            <div class="internal-login__logo-wrapper">
                <Logo class="internal-login__logo" />
            </div>

            <div class="internal-login__separator"></div>


            <div class="internal-login__form">
                <CoreStringInput
                    v-model="email"
                    :fullWidth="true"
                    placeholder="§ui.placeholder_login_email"
                    class="mb-6"
                />

                <CorePasswordInput
                    v-model="password"
                    :fullWidth="true"
                    class="mb-3"
                />

                <div class="login__checkbox mb-16">
                    <CoreCheckbox
                        v-model="remember"
                        name="remember_me"
                        label="§ui_button.remember_me"
                    />
                </div>

                <CoreButton
                    @click="handleSubmit"
                    label="§ui_button.sign_in"
                    icon="pi-arrow-up-right"
                    :loading="authStore.loading"
                    :disabled="authStore.loading"
                />
            </div>
        </main>
    </div>
</template>

<script>
import Logo from '@/assets/svg/FooterLogo.vue'
import { getUserBrowserPreferredLocale } from '@/helpers/mixins'
import CoreButton from '@/components/core/CoreButton.vue'
import CoreStringInput from '@/components/core/CoreStringInput.vue'
import CorePasswordInput from '@/components/core/CorePasswordInput.vue'
import CoreLinkPrimary from '@/components/core/CoreLinkPrimary.vue'
import CoreCheckbox from '@/components/core/CoreCheckbox.vue'
import CoreIcon from '@/components/core/CoreIcon.vue'
import { useAuthStore } from '@/stores/auth'

export default {
    name: 'InternalLogin',

    components: {
        Logo,
        CoreButton,
        CoreStringInput,
        CorePasswordInput,
        CoreLinkPrimary,
        CoreCheckbox,
        CoreIcon
    },

    mixins: [getUserBrowserPreferredLocale],

    data() {
        return {
            email: '',
            password: '',
            remember: false,
            waiting: false,
        }
    },

    created() {
        this.authStore = useAuthStore()
    },

    methods: {
        async handleSubmit() {
            try {
                if (this.waiting) return
                this.waiting = true

                await this.authStore.loginInternal({
                    email: this.email,
                    password: this.password,
                    remember: this.remember,
                })
            } catch (error) {
                this.waiting = false
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message || 'An unexpected error occurred',
                    life: 2000,
                })
            } finally {
                this.waiting = false
            }
        },
    },
}
</script>

<style>
.internal-login {
    min-height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
    background-size: cover;
    background-position: center;

    & .internal-login__content {
        position: relative;
        border-radius: 20px;
        isolation: isolate;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0px 6px 24px rgba(0, 0, 0, 0.2);
        backdrop-filter: blur(4px);
        -webkit-backdrop-filter: blur(4px);
        border: 1px solid rgba(255, 255, 255, 0.3);

        &::before {
            content: '';
            position: absolute;
            inset: 0;
            z-index: 0;
            border-radius: 20px;
            box-shadow: inset 0 0 20px -5px rgba(255, 255, 255, 0.6);
            background: rgba(255, 255, 255, 0.05);
        }

        &::after {
            content: '';
            position: absolute;
            inset: 0;
            z-index: -1;
            border-radius: 28px;
            backdrop-filter: blur(8px);
            isolation: isolate;
        }
        
        border-radius: 16px;
        padding: 0;
        width: 100%;
        max-width: 80rem;
        
        display: flex;
        justify-content: space-around;
        align-items: center;

        & .internal-login__separator {
            display: block;
            width: 1px;
            height: 80%;
            min-height: 260px;
            background: transparent;
            border-left: 1px solid rgba(255, 255, 255, 0.3);
            box-shadow: 0 0 8px rgba(255, 255, 255, 0.6);
        }

        & .internal-login__logo-wrapper {
            position: relative;
            z-index: 1;
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            max-width: 400px;
            padding: 5rem 2rem 5rem 2.8rem;
        }

        & .internal-login__form {
            position: relative;
            z-index: 1;
            width: 100%;
            max-width: 40rem;
            padding: 5rem;
            text-align: left;
        }
    }

    & .internal-login__logo {
        width: 240px;
        height: 240px;
        
        filter: drop-shadow(0 0 15px rgba(0, 0, 0, 0.4)) 
                drop-shadow(0 0 30px rgba(0, 0, 0, 0.2))
                drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
        
        & svg {
            backdrop-filter: blur(2px);
            -webkit-backdrop-filter: blur(2px);
            background: rgba(0, 0, 0, 0.1);
            border-radius: 12px;
            padding: 1rem;
            border: 1px solid rgba(0, 0, 0, 0.4);
            box-shadow: inset 0 0 15px rgba(0, 0, 0, 0.1),
                        inset 0 0 30px rgba(0, 0, 0, 0.05),
                        0 0 25px rgba(0, 0, 0, 0.15),
                        0 8px 16px rgba(0, 0, 0, 0.2),
                        0 0 40px rgba(255, 255, 255, 0.1);
        }
        
        & path {
            fill: rgba(0, 0, 0, 0.7) !important;
            filter: drop-shadow(0 2px 4px rgba(255, 255, 255, 0.1))
                    drop-shadow(0 0 8px rgba(255, 255, 255, 0.05));
        }
    }
}

@media (max-width: 768px) {
    .internal-login {
        align-items: flex-end;
        overflow-y: auto;
        padding-bottom: 10rem;

        & .internal-login__separator {
            display: none;
        }

        & .internal-login__logo {
            position: absolute;
            top: 10%;
            left: 50%;
            transform: translateX(-50%);

            width: 80%;
            max-width: 400px;
        }

        & .internal-login__content {
            padding: 2.5rem 2rem;
            width: 80%;

            & h1 {
                font-size: 3.2rem;
            }
        }
    }
}

@media (max-width: 480px) {
    .internal-login {
        align-items: flex-end;
        overflow-y: auto;
        padding-bottom: 0;

        & .internal-login__content {
            width: 100%;
            max-width: 100%;
            border-radius: 0;
            padding: 4rem 1rem;

            & > h1 {
                font-size: 2.8rem;
                margin-bottom: 4rem;
            }
        }
    }
}
</style>
